/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react';
import {
  useLazyQuery,
  useMutation,
  ApolloError,
  Reference,
  StoreObject,
} from '@apollo/client';
import { BackendTable, Pill } from '../../components';
import {
  dashboardContainer,
  FoundingEventStatus,
  paginationElements,
} from '../../utils/constants';
import {
  ManagedCompany,
  FoundingEventVar,
  FoundingEvent,
  PagingMetadata,
  OwnedCompany,
  SubscribedCompany,
} from '../../model';
import {
  GET_MANAGED_COMPANY_FOUNDING_EVENTS,
  GET_OWNED_COMPANY_FOUNDING_EVENTS,
  GET_SUBSCRIBED_COMPANY_FOUNDING_EVENTS,
} from '../../services/foundingEvents';
import { addRowNum } from '../../utils/formatData';
import { toLocaleDateTime } from '../../utils/formatTime';
import { Button } from '../../components/button';
import { useHistory } from 'react-router-dom';
import { Alert } from '../../components/alert';
import { useNotifications } from '../../hooks/notification';
import { NOTIFICATION } from '../../context/notificationContext';
import { DELETE_FOUNDING_EVENT } from '../../services/foundingEvents';
import { useUrlBuilder } from '../../hooks/urlBuilder';
import { handleEventStatus } from '../../utils/events';
import ReactLoading from 'react-loading';
import { Role } from '../../utils/constants';

const FoundingEvents = (): JSX.Element => {
  const { buildAdminUrl, selectedCompanyId, getRole } = useUrlBuilder();

  const { setNotification } = useNotifications();
  const [foundingEvents, setFoundingEvents] = useState([] as FoundingEvent[]);
  const [selectedEvent, setSelectedEvent] = useState({} as FoundingEvent);
  const [
    deleteConfirmationModalVisibility,
    setDeleteConfirmationModalVisibility,
  ] = useState(false);

  const [deleteFoundingEvent, { error: deleteFudingEventError }] = useMutation(
    DELETE_FOUNDING_EVENT,
    {
      update(cache, { data: { deleteFoundingEvent } }) {
        const { id } = deleteFoundingEvent;

        cache.modify({
          fields: {
            foundingEvents(existingFoundingEvents, { readField }) {
              return existingFoundingEvents.filter(
                (foundingEventRef: Reference | StoreObject | undefined) =>
                  id !== readField('id', foundingEventRef),
              );
            },
          },
        });
      },
    },
  );
  const [pageMetaData, setPageMetaData] = useState({} as PagingMetadata);
  const [sort, setSort] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const history = useHistory();

  const [
    getManagedCompanyFoundingEvents,
    {
      error: managedCompanyFoundingEventsError,
      refetch: refetchManagedCompanyFoundingEvents,
    },
  ] = useLazyQuery<ManagedCompany, FoundingEventVar>(
    GET_MANAGED_COMPANY_FOUNDING_EVENTS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (companyFoundingEvents) => {
        setPageMetaData(
          companyFoundingEvents.managedCompany.foundingEvents.metadata,
        );
        setFoundingEvents(
          addRowNum(
            companyFoundingEvents.managedCompany.foundingEvents.collection.map(
              (obj:FoundingEvent) => ({
                ...obj,
                eventOn: toLocaleDateTime(obj.eventOn, 'LL'),
              }),
            ),
          ),
        );
      },
      onError: () => setFoundingEvents([]),
    },
  );

  const [
    getOwnedCompanyFoundingEvents,
    {
      error: ownedCompanyFoundingEventsError,
      refetch: refetchOwnedCompanyFoundingEvents,
    },
  ] = useLazyQuery<OwnedCompany, FoundingEventVar>(
    GET_OWNED_COMPANY_FOUNDING_EVENTS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (companyFoundingEvents) => {
        setPageMetaData(
          companyFoundingEvents.ownedCompany.foundingEvents.metadata,
        );
        setFoundingEvents(
          addRowNum(
            companyFoundingEvents.ownedCompany.foundingEvents.collection.map(
              (obj: FoundingEvent) => ({
                ...obj,
                eventOn: toLocaleDateTime(obj.eventOn),
              }),
            ),
          ),
        );
      },
      onError: () => setFoundingEvents([]),
    },
  );

  const [
    getSubscribedCompanyFoundingEvents,
    {
      error: subscribedCompanyFoundingEventsError,
      refetch: refetchSubscribedCompanyFoundingEvents,
    },
  ] = useLazyQuery<SubscribedCompany, FoundingEventVar>(
    GET_SUBSCRIBED_COMPANY_FOUNDING_EVENTS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (companyFoundingEvents) => {
        setPageMetaData(
          companyFoundingEvents.subscribedCompany.foundingEvents.metadata,
        );
        setFoundingEvents(
          addRowNum(
            companyFoundingEvents.subscribedCompany.foundingEvents.collection.map(
              (obj: FoundingEvent) => ({
                ...obj,
                eventOn: toLocaleDateTime(obj.eventOn),
              }),
            ),
          ),
        );
      },
      onError: () => setFoundingEvents([]),
    },
  );

  const getFoundingEvents = () => {
    const role = getRole();

    switch (role) {
    case Role.ADMIN:
      getManagedCompanyFoundingEvents({
        variables: {
          companyId: selectedCompanyId,
          page: pageMetaData.currentPage ?? paginationElements.INITIAL_PAGE,
          limit: pageMetaData.limitValue ?? paginationElements.LIMIT,
          sort,
          search: searchValue,
        },
      });
      break;
    case Role.SUBSCRIBER:
      getSubscribedCompanyFoundingEvents({
        variables: {
          companyId: selectedCompanyId,
          page: pageMetaData.currentPage ?? paginationElements.INITIAL_PAGE,
          limit: pageMetaData.limitValue ?? paginationElements.LIMIT,
          sort,
          search: searchValue,
        },
      });
      break;
    case Role.SHAREHOLDER:
      getOwnedCompanyFoundingEvents({
        variables: {
          companyId: selectedCompanyId,
          page: pageMetaData.currentPage ?? paginationElements.INITIAL_PAGE,
          limit: pageMetaData.limitValue ?? paginationElements.LIMIT,
          sort,
          search: searchValue,
        },
      });
      break;
    }
  };

  useEffect(() => {
    if (
      selectedCompanyId &&
      !managedCompanyFoundingEventsError &&
      !ownedCompanyFoundingEventsError &&
      !subscribedCompanyFoundingEventsError
    ) {
      getFoundingEvents();
    }
  }, [selectedCompanyId, foundingEvents]);

  const columns = [
    {
      Header: 'Navn',
      accessor: 'name',
    },
    {
      Header: 'Dato',
      accessor: 'eventOn',
    },
    {
      Header: 'Antall nye aksjer',
      accessor: 'amount',
    },
    {
      Header: 'Egenkapital pr aksje',
      accessor: 'capitalPerShare',
    },
    {
      Header: 'Pris per aksje',
      accessor: 'pricePerShare',
    },
    {
      Header: 'Aksje premium',
      accessor: 'sharePremium',
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: 400,
      Cell: (props: any) => {
        return (
          <Pill
            variant={handleEventStatus(
              props.cell.row.original.status,
              props.cell.row.original.publicTransitionEvents,
            )}
            className="flex items-center justify-between normal-case"
          >
            <span className="mr-4">{props.cell.row.original.status.label}</span>
            {props.cell.row.original.status.background && (
              <ReactLoading
                type="spin"
                height={'10px'}
                width={'10px'}
                color="#4B2A58"
              />
            )}
          </Pill>
        );
      },
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: (props: any) => {
        return (
          <div className="flex">
            <Button
              label={<i className="uc-icon text-grey">&#xe972;</i>}
              presetStyle="mr-4"
              type="button-5"
              toExecute={() => {
                history.push(
                  buildAdminUrl(
                    `/founding-events/${props.cell.row.original.id}/edit`,
                  ),
                );
              }}
            />
            <Button
              toExecute={() =>
                history.push(
                  buildAdminUrl(
                    `/founding-events/${props.cell.row.original.id}`,
                  ),
                )
              }
              label={<i className="uc-icon text-grey">&#xe9a8;</i>}
              type="button-5"
            />
            {props.cell.row.original.status.label ===
              FoundingEventStatus.DRAFT && (
              <Button
                label={<i className="uc-icon text-grey">&#xeb97;</i>}
                presetStyle="ml-4"
                type="button-5"
                toExecute={() => {
                  setSelectedEvent(props.cell.row.original);
                  setDeleteConfirmationModalVisibility(true);
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  const removeFoundingEvent = (id: string) => {
    deleteFoundingEvent({
      variables: {
        id,
      },
    })
      .then((res) => {
        setNotification({ title: 'Slettet', type: NOTIFICATION.SUCCESS });
        // remove from the state.
        setFoundingEvents(foundingEvents.filter((item) => item.id !== id));
      })
      .catch((err: ApolloError) => {
        const role = getRole();
        if (role === Role.ADMIN && refetchManagedCompanyFoundingEvents)
          refetchManagedCompanyFoundingEvents();
        if (role === Role.SUBSCRIBER && refetchSubscribedCompanyFoundingEvents)
          refetchSubscribedCompanyFoundingEvents();
        if (role === Role.SHAREHOLDER && refetchOwnedCompanyFoundingEvents)
          refetchOwnedCompanyFoundingEvents();

        setNotification({
          title: 'Noe gikk galt, prøv igjen',
          type: NOTIFICATION.ERROR,
        });
      });
  };

  return (
    <>
      <Alert
        visible={deleteConfirmationModalVisibility}
        title={'Slett Emisjon'}
        message={'Er du sikker på at du vil slette emisjon?'}
        onCancel={() => setDeleteConfirmationModalVisibility(false)}
        okText="Slett"
        onOk={() => {
          setDeleteConfirmationModalVisibility(false);
          removeFoundingEvent(selectedEvent.id);
        }}
      />
      <div className={dashboardContainer}>
        <div className="mb-4 p-4 flex justify-between items-center">
          <div>
            <span className="font-hind font-bold text-5xl">Founding Events</span>
          </div>
          <div>
            <Button
              label={
                <>
                  Innskudd aksjekapital
                  <i className="uc-icon text-white ml-2">&#xead5;</i>
                </>
              }
              toExecute={() =>
                history.push(buildAdminUrl('/founding-events/new'))
              }
              type="button-2"
              presetStyle="mb-4"
            />
          </div>
        </div>
        <BackendTable
          columns={columns}
          data={foundingEvents}
          searchable
          paginate
          tableHeader
          onTableChange={() => getFoundingEvents()}
          setPageMetaData={setPageMetaData}
          pageMetaData={pageMetaData}
          setSortValue={setSort}
          sortValue={sort}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      </div>
    </>
  );
};

export default FoundingEvents;
